import React from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import { TaskStatusChip, TaskPriorityChip } from "../../components/TaskChips";
import { getContactNameDisplay } from "../../utils/contactPhoneDisplay";

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(1.5),
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  title: {
    fontWeight: 600,
    overflowWrap: "break-word",
    wordBreak: "break-word"
  },
  description: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    margin: theme.spacing(1, 0)
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5)
  }
}));

// Cartão vertical usado na listagem em telas pequenas (celular) — evita a
// tabela larga com rolagem horizontal obrigatória. Clicável com mouse e
// teclado (Enter/Espaço); Espaço usa preventDefault para não rolar a
// página. Qualquer botão interno futuro precisa de stopPropagation.
const TaskCard = ({ task, onClick }) => {
  const classes = useStyles();

  const handleKeyDown = e => {
    if (typeof onClick !== "function") return;
    if (e.key === "Enter") {
      onClick();
    } else if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      className={classes.card}
      variant="outlined"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${i18n.t("tasks.details.title")}: ${task.title}`}
    >
      <CardContent>
        <Typography variant="subtitle1" className={classes.title}>
          {task.title}
        </Typography>

        {task.description && (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.description}
          >
            {task.description}
          </Typography>
        )}

        <Box className={classes.chipsRow}>
          <TaskStatusChip status={task.status} />
          <TaskPriorityChip priority={task.priority} />
        </Box>

        <Box className={classes.metaRow}>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("tasks.table.assignedUser")}: {task.assignedUser?.name || "-"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("tasks.table.dueDate")}:{" "}
            {task.dueDate ? moment(task.dueDate).format("DD/MM/YYYY") : i18n.t("tasks.table.noDueDate")}
          </Typography>
        </Box>

        {(task.contact || task.ticket) && (
          <Box className={classes.metaRow}>
            {task.contact && (
              <Typography variant="caption" color="textSecondary">
                {i18n.t("tasks.table.contact")}: {getContactNameDisplay(task.contact)}
              </Typography>
            )}
            {task.ticket && (
              <Typography variant="caption" color="textSecondary">
                {i18n.t("tasks.table.ticket")}: #{task.ticket.uuid?.slice(0, 8) || task.ticket.id}
              </Typography>
            )}
          </Box>
        )}

        <Box className={classes.metaRow}>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("tasks.table.createdAt")}: {moment(task.createdAt).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
